import { useMemo } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { BookOpen, ChevronRight, ExternalLink, Plus, Presentation } from 'lucide-react'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { ScrollArea } from '@/components/ui/scroll-area'
import { AppIcon } from '@/components/common/icon-map'
import { useIssueFields, useIssues } from '@/lib/query'
import { INSIGHTS_LENSES, INSIGHTS_VIEW, VIEWS, savedSearchGroups } from '@/lib/views'
import { currentSprint } from '@/lib/sprint'
import { useWorkDocs } from '@/hooks/use-work-docs'
import { useSavedSearches } from '@/hooks/use-saved-searches'
import { SavedSearchItem } from '@/components/saved-search/saved-search-item'
import { useTeams } from '@/hooks/use-teams'
import { fieldByName } from '@/lib/query'
import { useMe } from '@/hooks/use-me'

function useLastArea(): string | null {
  try {
    return window.localStorage.getItem('n3o-work-last-area')
  } catch {
    return null
  }
}

export function AppSidebar() {
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const issues = useIssues().data
  const fields = useIssueFields().data
  const lastArea = useLastArea()
  const me = useMe()
  const workDocs = useWorkDocs()
  const teams = useTeams()
  const savedSearches = useSavedSearches().data

  const sprintName = useMemo(() => {
    const options = fieldByName(fields, 'Sprint')?.options.map((o) => o.name) ?? []
    return currentSprint(options)?.name ?? null
  }, [fields])

  const groups = useMemo(
    () =>
      savedSearchGroups({
        teams,
        clients: fieldByName(fields, 'Client')?.options.map((o) => o.name) ?? [],
        products: fieldByName(fields, 'Product')?.options.map((o) => o.name) ?? [],
        sources: fieldByName(fields, 'Source')?.options.map((o) => o.name) ?? [],
      }),
    [fields, teams],
  )

  const counts = useMemo(() => {
    const sprintPath = pathname.startsWith('/sprint/') ? pathname.slice('/sprint/'.length) : null
    const ctx = {
      currentSprintName: sprintPath ? decodeURIComponent(sprintPath) : sprintName,
      viewerLogin: me?.login,
      fields,
      sprintGroupBy: pathname.startsWith('/sprint/') || pathname === '/sprint'
        ? (searchParams.get('group') === 'Priority' ? 'Priority' as const : 'Stage' as const)
        : 'Stage' as const,
    }
    const meCtx = pathname === '/me' ? {
      ...ctx,
      viewerLogin: searchParams.get('user') ?? me?.login,
      meMode: searchParams.get('mode') === 'reported' ? 'reported' as const : 'assigned' as const,
    } : ctx
    return new Map(VIEWS.map((v) => [v.key, issues ? v.pool(issues, v.key === 'me' ? meCtx : ctx).length : 0]))
  }, [issues, sprintName, me?.login, fields, pathname, searchParams])

  const activeFilter = pathname === '/q' ? searchParams.get('filter') : null

  const insightsWindow = searchParams.get('window')
  const insightsSearch = insightsWindow ? `?window=${insightsWindow}` : ''

  return (
    <Sidebar collapsible="icon">
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Issues</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {VIEWS.map((view) => {
                const target = view.key === 'team' ? (lastArea ? `/team/${lastArea}` : '/team') : view.path
                const isActive = pathname === view.path || pathname.startsWith(view.path + '/')
                const count = counts.get(view.key) ?? 0
                const showDot = view.key === 'triage' && count > 0
                return (
                  <SidebarMenuItem key={view.key}>
                    <SidebarMenuButton asChild isActive={isActive} tooltip={view.title}>
                      <Link to={target}>
                        <AppIcon name={view.icon} className="size-4" />
                        <span>{view.title}</span>
                      </Link>
                    </SidebarMenuButton>
                    {showDot ? (
                      <SidebarMenuBadge>
                        <span className="bg-primary inline-block size-1.5 rounded-full" aria-label={`${count} items`} />
                      </SidebarMenuBadge>
                    ) : view.key !== 'team' && count > 0 ? (
                      <SidebarMenuBadge className="text-faint-foreground font-mono text-[10.5px]">
                        {count}
                      </SidebarMenuBadge>
                    ) : null}
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="group-data-[collapsible=icon]:hidden">
          <SidebarGroupLabel>Saved searches</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={pathname === '/q' && !activeFilter}
                  tooltip="New search"
                  className="text-muted-foreground"
                >
                  <Link to="/q">
                    <Plus className="size-4" />
                    <span>New search</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {[...(savedSearches?.org ?? []), ...(savedSearches?.mine ?? [])].map((s) => (
                <SavedSearchItem
                  key={`${s.scope}:${s.owner ?? 'org'}:${s.slug}`}
                  search={s}
                  isActive={activeFilter === s.filter}
                />
              ))}
              {groups.map((group) => {
                const scrolls = group.items.length > 8
                const items = group.items.map((item) => {
                  const href = `/q?filter=${encodeURIComponent(item.filter)}&name=${encodeURIComponent(item.label)}`
                  const isActive = activeFilter === item.filter
                  return (
                    <SidebarMenuSubItem key={item.filter}>
                      <SidebarMenuSubButton asChild isActive={isActive}>
                        <Link to={href}>
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  )
                })
                return (
                  <Collapsible key={group.title} className="group/collapsible" asChild>
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton>
                          <AppIcon name={group.icon} className="size-4" />
                          <span>{group.title}</span>
                          <ChevronRight className="ml-auto size-3.5 transition-transform group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {scrolls ? <ScrollArea className="h-42 pr-1">{items}</ScrollArea> : items}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Insights</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {INSIGHTS_LENSES.filter((lens) => !lens.managerOnly || me?.isManager).map((lens) => {
                const isActive =
                  lens.path === INSIGHTS_VIEW.path
                    ? pathname === lens.path
                    : pathname === lens.path || pathname.startsWith(lens.path + '/')
                return (
                  <SidebarMenuItem key={lens.key}>
                    <SidebarMenuButton asChild isActive={isActive} tooltip={lens.title}>
                      {/* Window carries across lenses; drill-down params don't. */}
                      <Link to={{ pathname: lens.path, search: insightsSearch }}>
                        <AppIcon name={lens.icon} className="size-4" />
                        <span>{lens.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="mt-auto py-0 group-data-[collapsible=icon]:hidden">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild className="h-7 text-xs font-medium">
                  {/* Opens the deck in its own tab — it runs full-window there,
                      no iframe needed. */}
                  <a
                    href="/api/work-files/presentation/index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Presentation className="size-3.5" />
                    <span>How we work</span>
                    <ExternalLink className="ml-auto size-3 opacity-40" />
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className="py-0 group-data-[collapsible=icon]:hidden">
          <SidebarGroupLabel>Guides</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {workDocs
                .filter((d) => d.inSidebar)
                .map((d) => (
                  <SidebarMenuItem key={d.slug}>
                    <SidebarMenuButton asChild isActive={pathname === `/docs/${d.slug}`} className="h-6 text-xs">
                      <Link to={`/docs/${d.slug}`}>
                        <BookOpen className="size-3.5" />
                        <span>{d.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  )
}
